import React from "react";
import MapControl from "@mrwa/rime-replacement/esriControls/Map";
import mapHelper from "@mrwa/rime-replacement/infrastructure/mapHelper";
import requestEsriModules from "@mrwa/rime-replacement/infrastructure/esriModules";
import crashState from "../roadSafety/crashState";
import { formatterDate, formatterDay, formatterTime24 } from "../data/misc";
import {
  getMap,
  getTools,
  getControls,
  getBehaviours,
} from "@mrwa/rime-replacement/infrastructure/configHelper";
import classNames from "classnames";
import CrashData from "../roadSafety/CrashData";
import BaseMapSelector from "./BaseMapSelector";
import { ToolStyle } from "@mrwa/rime-replacement/esriControls/unions";
import CrashLayer from "@mrwa/crashmap/esriControls/crashmapTools/crashLayer";
/* 2745 - remove functionality that saves the user's visible layers. Leaving the code in case it
      is requested to be made configurable in future */
//import { isMatchingLayer } from "@mrwa/rime-replacement/esriControls/controls/layer/layerSynchronisationImpl";
import CrashDetailsPopupControl from "@mrwa/crashmap/esriControls/crashmapTools/query/CrashDetailsPopupControl";
import CollisionDiagramsOverpanel from "@mrwa/crashmap/esriControls/crashmapTools/collisionDiagrams/CollisionDiagramsOverpanel";
import QuerySummaryPanel from "@mrwa/crashmap/esriControls/crashmapTools/querySummary/querySummaryControl";
import CrashDependentComponent from "./CrashDependentComponent";
import { CrashContext } from "../context";
import collisionDiagramImages from "../roadSafety/collisionDiagramImages";
import LayerControl from "./LayerControl";
import AddCustomLayerTool from "@mrwa/rime-replacement/esriControls/tools/AddCustomLayerTool";

import "@mrwa/rime-replacement/esriControls/general/tabControl.css";
import "./mapControl.css";

function initialiseExternalLayer(l, index, severityDisplaySettings) {
  switch (l.layerType) {
    case CrashLayer.layerType:
      return new CrashLayer(
        l.uniqueIdentifier,
        l.url,
        l.overrideVisible,
        l.selectionLayerId,
        index,
        severityDisplaySettings,
        l.symbologySwitchScale
      );
    default:
      return null;
  }
}

function initialiseExternalControl(
  createCrashDependantComponent,
  type,
  contents
) {
  switch (type) {
    case BaseMapSelector.componentName:
      return ToolStyle.React((view) => ({
        reactElement: (
          <BaseMapSelector view={view} basemaps={contents.basemaps} />
        ),
        position: contents.position ?? "bottom-right",
        containerClass: "no-shadow",
      }));
    case CrashDetailsPopupControl.controlName:
      return ToolStyle.React((view) => ({
        reactElement: createCrashDependantComponent(
          <CrashDetailsPopupControl
            view={view}
            crashDetailsState={crashState}
            formatterDay={formatterDay}
            formatterDate={formatterDate}
            formatterTime={formatterTime24}
          />
        ),
        position: "manual",
        containerClass: "overPanel",
      }));
    case CollisionDiagramsOverpanel.componentName:
      return ToolStyle.React((view) => ({
        reactElement: createCrashDependantComponent(
          <CollisionDiagramsOverpanel
            view={view}
            crashDetailsState={crashState}
            images={collisionDiagramImages}
          />
        ),
        position: "manual",
        containerClass: "overPanel collisionDiagramOverPanel",
      }));
    case QuerySummaryPanel.componentName:
      return ToolStyle.React((view) => ({
        reactElement: createCrashDependantComponent(
          <QuerySummaryPanel view={view} crashDetailsState={crashState} />
        ),
        position: "manual",
        containerClass: "overPanel querySummaryOverPanel",
      }));
    case LayerControl.componentName:
      return ToolStyle.React((view) => ({
        reactElement: <LayerControl view={view} settings={contents} />,
        position: contents.position,
        containerClass: "selfScroll",
      }));
    default:
      return null;
  }
}

function initialiseExternalTool(type) {
  switch (type) {
    case AddCustomLayerTool.componentName:
      return new ToolStyle.React((view) => ({
        reactElement: <AddCustomLayerTool view={view} />,
      }));
    default:
      return null;
  }
}

async function generateCarIcon(graphicsLayer, geometry) {
  const { Graphic } = await requestEsriModules({
    Graphic: "esri/Graphic",
  });

  const symbol = {
    type: "picture-marker",
    width: "26px",
    height: "26px",
    angle: "-45",
    url: `${process.env.PUBLIC_URL}/roadview_full.png`,
  };
  const carIcon = new Graphic({
    geometry: geometry,
    symbol: symbol,
  });
  graphicsLayer.graphics.add(carIcon);

  return carIcon;
}

/* 2745 - remove functionality that saves the user's visible layers. Leaving the code in case it
      is requested to be made configurable in future */
//const visibleLayerStorageItem = "RoadView::visibleLayers";
const carSymbolGraphicsLayerId = "carSymbolGraphicsLayer";
const routeFeaturesGraphicsLayerId = "routeFeaturesGraphicsLayer";

export default class RoadViewMapControl extends React.Component {
  static contextType = CrashContext;
  constructor(props) {
    super(props);

    this.state = {
      map: null,
      controls: null,
      initialised: null,
      behaviours: null,
      mapInitialised: null,
    };

    this.crashChangeComponentCallbacks = [];

    this.mapInitialised = this.mapInitialised.bind(this);
    this.layersChanged = this.layersChanged.bind(this);
  }

  componentDidMount() {
    if (this.props.mapSettings) {
      this.initialise();
    }

    this.setupPlayerContext();
  }

  componentDidUpdate(prevProps) {
    if (!this.state.initialised && this.props.mapSettings) {
      this.initialise();
    }

    if (this.props.playerContext !== prevProps.playerContext) {
      this.setupPlayerContext();
    }

    this.crashChangeComponentCallbacks.forEach((callback) =>
      callback(this.context.isCrashUser)
    );
  }

  setupPlayerContext() {
    if (this.props.playerContext) {
      this.props.playerContext.onGpsDataChanged(this.gpsDataChanged.bind(this));
      this.props.playerContext.onGpsFileChanged(this.gpsFileChanged.bind(this));
      this.props.playerContext.onContextStateChanged((context) => {
        if (this.routeFeaturesGraphicsLayer)
          this.routeFeaturesGraphicsLayer.visible = context.playing;
        //hide identify and measure panel on context change (when user plays or changes video position, but any context change is fine)
        mapHelper.featureCollections.identifyResults = [];
        if (mapHelper.activeTool) mapHelper.activeTool = null;
      });
    }
  }

  initialise() {
    this.expectedParentLayers = this.props.mapSettings.map.layers.map(
      (layerInfo) => layerInfo.uniqueIdentifier
    );
    /* 2745 - remove functionality that saves the user's visible layers. Leaving the code in case it
      is requested to be made configurable in future */
    // const savedVisibleLayers = localStorage.getItem(visibleLayerStorageItem);
    // this.initialVisibleLayers = savedVisibleLayers
    //   ? JSON.parse(savedVisibleLayers).map((layerIdentifier) => ({
    //       layerIdentifier,
    //       applied: false,
    //     }))
    //   : [];
    mapHelper.subscribe("layers", this.layersChanged);

    const initialiseExternalLayerBound = (l, index) =>
      initialiseExternalLayer(
        l,
        index,
        this.props.mapSettings.severityDisplaySettings
      );

    this.setState({
      initialised: true,
      map: getMap(this.props.mapSettings.map, initialiseExternalLayerBound),
      controls: getControls(
        this.props.mapSettings.controls,
        initialiseExternalControl.bind(
          null,
          this.createCrashDependantComponent.bind(this)
        )
      ),
      tools: getTools(this.props.mapSettings.tools, initialiseExternalTool),
      behaviours: getBehaviours(this.props.mapSettings.behaviours),
    });
  }

  createCrashDependantComponent(children) {
    return (
      <CrashDependentComponent
        isCrashUser={this.context.isCrashUser}
        bindCrashUserChange={this.addCrashChangeCallback.bind(this)}
      >
        {children}
      </CrashDependentComponent>
    );
  }

  addCrashChangeCallback(callback) {
    this.crashChangeComponentCallbacks.push(callback);
  }

  async layersChanged(layers) {
    /* 2745 - remove functionality that saves the user's visible layers. Leaving the code in case it
      is requested to be made configurable in future */
    // if (this.initialVisibleLayers.every(({ applied }) => applied)) {
    //   //initial reload of visible status of layers has finished, so keep track of visiblity changes on layers we care about
    //   const visibleLayers = layers
    //     .filter(
    //       (rimeLayer) =>
    //         rimeLayer.layer.loaded &&
    //         rimeLayer.visible &&
    //         //only want to set for sublayers, not parent layers, otherwise new parent layers won't show by default
    //         this.expectedParentLayers.find(
    //           (uniqueIdentifier) =>
    //             rimeLayer.topParent &&
    //             rimeLayer.topParent().uniqueIdentifier === uniqueIdentifier
    //         )
    //     )
    //     .map((rimeLayer) => ({
    //       uniqueIdentifier: rimeLayer.topParent().uniqueIdentifier,
    //       subLayerId: rimeLayer.id,
    //     }));
    //   localStorage.setItem(
    //     visibleLayerStorageItem,
    //     JSON.stringify(visibleLayers)
    //   );

    //handle adding/removing route graphics according to visible layers
    if (!this.graphicsByLayer) return;
    for (let rimeLayer of layers) {
      if (
        !rimeLayer.layer.loaded ||
        !this.expectedParentLayers.find(
          (uniqueIdentifier) =>
            rimeLayer.topParent &&
            rimeLayer.topParent().uniqueIdentifier === uniqueIdentifier
        )
      )
        continue;

      if (!rimeLayer.visible && this.graphicsByLayer.has(rimeLayer)) {
        //layer has been made not visible, remove graphics
        this.routeFeaturesGraphicsLayer.removeMany(
          this.graphicsByLayer.get(rimeLayer)
        );
        this.graphicsByLayer.delete(rimeLayer);
      }
      if (
        rimeLayer.visible &&
        this.showFeaturesAsGraphics &&
        !this.graphicsByLayer.has(rimeLayer)
      ) {
        this.showFeaturesAsGraphics(rimeLayer);
      }
    }
    /* 2745 - remove functionality that saves the user's visible layers. Leaving the code in case it
      is requested to be made configurable in future */
    // } else {
    //   //still loading intial visibility - set all layers that have loaded
    //   layers
    //     .filter((rimeLayer) => rimeLayer.layer.loaded)
    //     .forEach((loadedLayer) => {
    //       const initialVisibility = this.initialVisibleLayers.find(
    //         ({ layerIdentifier, applied }) =>
    //           !applied && isMatchingLayer(layerIdentifier, loadedLayer)
    //       );
    //       if (initialVisibility) {
    //         initialVisibility.applied = true;
    //         loadedLayer.visible = true;
    //       }
    //     });
    // }
  }

  setLayerVisibility() {
    mapHelper.layers.forEach((layer) => {
      for (let item in localStorage) {
        if (item === layer.name) {
          layer.visible = localStorage.getItem(item) === "true";
        }
      }
    });
  }

  async showFeaturesAsGraphics(rimeLayer) {
    if (
      !rimeLayer.layer ||
      !rimeLayer.layer.queryFeatures ||
      !this.graphicsByLayer
    )
      return;
    //ensure this function doesn't get entered again while performing async operations
    this.graphicsByLayer.set(rimeLayer, []);
    const { Graphic } = await requestEsriModules({
      Graphic: "esri/Graphic",
    });

    const bufferedGeometry = await this.props.playerContext.gpsInformation
      .bufferedGeometry;
    const query = {
      geometry: bufferedGeometry,
      spatialRelationship: "intersects",
      outFields: ["*"],
      returnGeometry: true,
    };

    const features = (await rimeLayer.layer.queryFeatures(query)).features;
    const graphics = await Promise.all(
      features.map(async (feature) => {
        const symbol = await feature.sourceLayer.renderer.getSymbolAsync(
          feature
        );
        return new Graphic({ geometry: feature.geometry, symbol });
      })
    );

    this.graphicsByLayer.set(rimeLayer, graphics);
    //remove all and readd in z-order
    this.routeFeaturesGraphicsLayer.removeAll();
    Array.from(this.graphicsByLayer.keys())
      .sort(
        (rl1, rl2) =>
          mapHelper.layers.indexOf(rl2) - mapHelper.layers.indexOf(rl1)
      )
      .forEach((rimeLayer) =>
        this.routeFeaturesGraphicsLayer.addMany(
          this.graphicsByLayer.get(rimeLayer)
        )
      );
  }

  async gpsFileChanged() {
    this.routeFeaturesGraphicsLayer.removeAll();
    this.graphicsByLayer = new Map();
    mapHelper.layers
      .filter(
        (rimeLayer) =>
          rimeLayer.visible &&
          this.expectedParentLayers.find(
            (uniqueIdentifier) =>
              rimeLayer.topParent &&
              rimeLayer.topParent().uniqueIdentifier === uniqueIdentifier
          )
      )
      .forEach((rimeLayer) => this.showFeaturesAsGraphics(rimeLayer));
  }

  async gpsDataChanged(gpsData) {
    if (!gpsData || !gpsData.isValidGeometry) return;

    const { projectedGeometry, bearingValue } = gpsData;
    const currentPoint = await projectedGeometry;

    const carIcon =
      this.carGraphicsLayer.graphics.items[0] ??
      (await generateCarIcon(this.carGraphicsLayer, currentPoint));

    carIcon.symbol.angle = bearingValue - 90;
    carIcon.geometry = currentPoint;

    mapHelper.zoomToGeometry(currentPoint);
  }

  async mapInitialised() {
    const { GraphicsLayer } = await requestEsriModules({
      GraphicsLayer: "esri/layers/GraphicsLayer",
    });
    this.carGraphicsLayer = new GraphicsLayer({ id: carSymbolGraphicsLayerId });
    this.routeFeaturesGraphicsLayer = new GraphicsLayer({
      id: routeFeaturesGraphicsLayerId,
      visible: false,
    });

    mapHelper.mapView.map.add(this.routeFeaturesGraphicsLayer);
    mapHelper.mapView.map.add(this.carGraphicsLayer);
    //ensure the car graphic is always on top. Since this layer isn't handled through RIME, we need to go directly to the map
    setInterval(() => {
      const index = mapHelper.mapView.map.layers.indexOf(this.carGraphicsLayer);
      if (index !== mapHelper.mapView.map.layers.length - 1)
        mapHelper.mapView.map.reorder(
          this.carGraphicsLayer,
          mapHelper.mapView.map.layers.length - 1
        );
    }, 1000);

    //prevent the xsmall setting from kicking in, which messes with the app's styles
    mapHelper.mapView.breakpoints.xsmall = 0;

    this.setState({ mapInitialised: true });
    this.props.mapInitialised && this.props.mapInitialised();
  }

  render() {
    if (!this.state.map) return null;

    const className = classNames("mapControl", {
      "video-playing": this.props.playing,
    });
    return (
      <div className={className}>
        <div className="mapContainer" style={{ flex: 1 }}>
          <MapControl
            map={this.state.map}
            tools={[...this.state.tools, ...this.state.controls]}
            behaviours={this.state.behaviours}
            commands={[]}
            appName="RoadView"
            initialised={this.mapInitialised}
          />
          {this.context.isCrashUser &&
            this.props.mapSettings &&
            this.state.mapInitialised && (
              <CrashData
                playerContext={this.props.playerContext}
                mapSettings={this.props.mapSettings}
                accountId={this.context.accountId}
              />
            )}
        </div>
      </div>
    );
  }
}
